/**
 * FERP Portal - User Menu Items Hook
 *
 * Backend-driven user menu items for the avatar dropdown.
 * Combines two data sources (same pattern as sidebar navigation):
 *
 * 1. Setting Bundle (`settingBundle.userMenuItems`): Defines WHAT items exist
 *    per industry. Parsed from st-0-0.yaml `navigation.user_menu_items`.
 *    NVO lever 6 can rename/hide items per tenant.
 *
 * 2. Allowed Navigation (`allowedNavigation.userScopes`): Provides the user's
 *    resolved scopes for visibility filtering.
 *
 * Architecture:
 * - Setting YAML defines user menu items (industry-specific configuration)
 * - NVO overrides customize per tenant (lever 6: rename/hide)
 * - Frontend filters by required_scopes against user's resolved scopes
 * - Routes for each item are scope-gated in seed_subgraph_data.rs
 *
 * Legislation backing (MN ALF):
 * - My Access: MN 144G.71 Subd 6-7 (delegation), 144G.45 (competency)
 * - My Training: MN 144G.45 Subd 3 (annual training), 144G.72 (dementia)
 * - My Payroll: FLSA 29 USC 207 (overtime), MN 181.032 (earnings statements)
 *
 * FERP Principles:
 * - Backend authority: Items defined in Setting YAML, not frontend code
 * - Scope-gated: Items with required_scopes hidden if user lacks those scopes
 * - NVO-customizable: Per-tenant rename/hide via lever 6
 * - Fail-closed: Empty array while loading (DRR-102/DRR-123)
 */

import { useMemo } from 'react';
import {
  useGetSettingNavigationQuery,
  useGetAllowedNavigationQuery,
  ViewLayer as GqlViewLayer,
} from '@/generated/graphql';
import { useAuth, useViewLayer } from '@/auth/AuthContext';

export interface UserMenuItem {
  id: string;
  label: string;
  path: string;
  icon: string;
  sortOrder: number;
}

/**
 * Returns scope-filtered user menu items for the avatar dropdown.
 *
 * Items come from the Setting Bundle (backend-driven, NVO-customized).
 * Visibility is determined by comparing each item's `requiredScopes`
 * against the user's resolved scopes from `allowedNavigation`.
 *
 * Falls back to empty array while data is loading (fail-closed).
 */
export function useUserMenuItems(): {
  items: UserMenuItem[];
  loading: boolean;
} {
  const { tenant } = useAuth();
  const viewLayer = useViewLayer();
  const tenantId = tenant?.id;

  // Source 1: User menu items from Setting YAML (industry-specific, NVO-customized)
  const { data: settingNavData, loading: settingLoading } = useGetSettingNavigationQuery({
    skip: !tenantId,
  });

  // Source 2: User's resolved scopes from AllowedNavigation
  const gqlLayer = viewLayer.toUpperCase() as GqlViewLayer;
  const { data: allowedNavData, loading: scopeLoading } = useGetAllowedNavigationQuery({
    variables: { tenantId: tenantId ?? '', layer: gqlLayer },
    skip: !tenantId,
  });

  const items = useMemo(() => {
    const rawItems = settingNavData?.settingBundle?.userMenuItems ?? [];
    const userScopes = new Set(allowedNavData?.allowedNavigation?.userScopes ?? []);
    const hasWildcard = userScopes.has('*') || userScopes.has('*:*');

    return rawItems
      .filter((item) => {
        // Items with no required scopes are always visible
        if (!item.requiredScopes || item.requiredScopes.length === 0) {
          return true;
        }
        // Wildcard scope grants access to all scope-gated items
        if (hasWildcard) {
          return true;
        }
        // User must have at least one of the required scopes
        return item.requiredScopes.some((scope) => userScopes.has(scope));
      })
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((item) => ({
        id: item.id,
        label: item.label,
        path: item.path,
        icon: item.icon,
        sortOrder: item.sortOrder,
      }));
  }, [settingNavData, allowedNavData]);

  return {
    items,
    loading: settingLoading || scopeLoading,
  };
}
